/**
 * cart-drawer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom Shopify Cart Drawer
 *  • Intercepts .shopify-product-form submissions (stops theme events)
 *  • Custom event bus: cart:open, cart:close, cart:updated, cart:item-added,
 *    cart:item-removed, cart:item-changed, cart:error
 *  • Shopify AJAX Cart API wrapper
 *  • Free shipping progress bar
 *  • Free gift nudge
 *  • Discount code (Shopify storefront discounts applied via URL param)
 *  • Quantity +/- with debounced API calls
 *  • Upsell "Add all to cart"
 * ─────────────────────────────────────────────────────────────────────────────
 * Place in assets/ and load at the bottom of theme.liquid:
 *   {{ 'cart-drawer.css' | asset_url | stylesheet_tag }}
 *   {{ 'cart-drawer.js'  | asset_url | script_tag }}
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ─── Guard: run only once ──────────────────────────────────── */
  if (window.__CartDrawerInitialised) return;
  window.__CartDrawerInitialised = true;

  /* ─── Config from liquid ────────────────────────────────────── */
  const CFG = window.CartDrawerConfig || {};
  const ROUTES = CFG.routes || {
    cartAdd:    '/cart/add.js',
    cartGet:    '/cart.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js',
  };

  /* ══════════════════════════════════════════════════════════════
     EVENT BUS
     Custom events are dispatched on document.
     Listen: document.addEventListener('cart:updated', handler)
     Emit:   CartEvents.emit('cart:updated', payload)
  ══════════════════════════════════════════════════════════════ */
  const CartEvents = {
    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    },
    on(name, handler) {
      document.addEventListener(name, handler);
    },
    off(name, handler) {
      document.removeEventListener(name, handler);
    },
  };
  window.CartEvents = CartEvents; // expose globally

  /* ══════════════════════════════════════════════════════════════
     AJAX CART API
  ══════════════════════════════════════════════════════════════ */
  const CartAPI = {
    async _fetch(url, options = {}) {
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...options,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
      return data;
    },

    get() {
      return this._fetch(ROUTES.cartGet);
    },

    add(items) {
      // items: [{ id, quantity, properties? }] or single { id, quantity }
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      return this._fetch(ROUTES.cartAdd, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    change(line, quantity, properties) {
      return this._fetch(ROUTES.cartChange, {
        method: 'POST',
        body: JSON.stringify({ line, quantity, properties }),
      });
    },

    changeByKey(key, quantity) {
      return this._fetch(ROUTES.cartChange, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity }),
      });
    },

    update(updates) {
      return this._fetch(ROUTES.cartUpdate, {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
    },
    applyDiscount(code) {
  return this._fetch(ROUTES.cartUpdate, {
    method: 'POST',
    body: JSON.stringify({ discount: code }),
  });
},
  };
  window.CartAPI = CartAPI; // expose globally

  /* ══════════════════════════════════════════════════════════════
     UTILITY HELPERS
  ══════════════════════════════════════════════════════════════ */

  /** Format money using Shopify's money_format string (basic) */
  function formatMoney(cents) {
    const fmt = (CFG.moneyFormat || '£{{amount}}').replace('{{amount_no_decimals}}',
      Math.round(cents / 100));
    const amount = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount)
               .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  /** Debounce */
  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  /** Simple toast */
  let toastEl;
  function showToast(msg, duration = 2400) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cd-toast';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(() => toastEl.classList.remove('is-visible'), duration);
  }

  /* ══════════════════════════════════════════════════════════════
     DOM REFERENCES
  ══════════════════════════════════════════════════════════════ */
  const drawer      = document.getElementById('cart-drawer');
  const overlay     = document.getElementById('cart-drawer-overlay');
  const closeBtn    = document.getElementById('cd-close');
  const itemsList   = document.getElementById('cd-items');
  const emptyEl     = document.getElementById('cd-empty');
  const countEl     = document.getElementById('cd-item-count');
  const subtotalEl  = document.getElementById('cd-subtotal');
  const shippingBar = document.getElementById('cd-shipping-bar');
  const shippingMsg = document.getElementById('cd-shipping-text');
  const shippingFill= document.getElementById('cd-shipping-fill');
  const giftNudge   = document.getElementById('cd-gift-nudge');
  const giftText    = document.getElementById('cd-gift-text');
  const discountInput = document.getElementById('cd-discount-input');
  const discountApply = document.getElementById('cd-discount-apply');
  const discountMsg   = document.getElementById('cd-discount-msg');
  const checkoutBtn   = document.getElementById('cd-checkout');
  const upsellBtn     = document.getElementById('cd-upsell-btn');
  const upsellSummaryCompare = document.querySelector('.cd-upsell__summary-compare');
const upsellSummaryPrice   = document.querySelector('.cd-upsell__summary-price');
const upsellSummarySave    = document.querySelector('.cd-upsell__summary-save');
const upsellBadge = document.querySelector('.cd-upsell__badge');

  if (!drawer) return; // snippet not rendered

  /* spinner */
  const spinner = document.createElement('div');
  spinner.className = 'cd-spinner';
  spinner.innerHTML = '<div class="cd-spinner__ring"></div>';
  drawer.appendChild(spinner);

  /* ══════════════════════════════════════════════════════════════
     OPEN / CLOSE
  ══════════════════════════════════════════════════════════════ */
  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cd-no-scroll');
    // Move focus into drawer
    closeBtn && closeBtn.focus();
    CartEvents.emit('cart:open');
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-visible');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cd-no-scroll');
    CartEvents.emit('cart:close');
  }

  function setLoading(state) {
    spinner.classList.toggle('is-active', state);
  }

  /* ── Close triggers ─────────────────────────────────────────── */
  closeBtn  && closeBtn.addEventListener('click', closeDrawer);
  overlay   && overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  /* Focus trap inside drawer */
  drawer.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = drawer.querySelectorAll(
      'a,button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  });

  /* ══════════════════════════════════════════════════════════════
     RENDER CART
  ══════════════════════════════════════════════════════════════ */
  function renderCart(cart) {
    const isEmptyCart = cart.item_count === 0;

  drawer.classList.toggle('cd-drawer--empty', isEmptyCart);
    // Count
    if (countEl) countEl.textContent = `(${cart.item_count})`;

    // Update page header cart count (theme bubbles)
    document.querySelectorAll('[data-cart-count],[data-cart-item-count],.cart-count,.header__cart-count')
      .forEach(el => { el.textContent = cart.item_count; });

    // Subtotal
    // Subtotal
if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

// Discount pill after reload
const activeDiscountCode = getCartDiscountCode(cart);

if (activeDiscountCode) {
  sessionStorage.setItem('cd_discount', activeDiscountCode);
  renderDiscountPill(activeDiscountCode);
} else if (discountMsg) {
  sessionStorage.removeItem('cd_discount');
  discountMsg.innerHTML = '';
  discountMsg.className = 'cd-discount__msg';
}

    // Checkout URL: append discount if stored
    const storedDiscount = getCartDiscountCode(cart);

if (checkoutBtn) {
  checkoutBtn.href = storedDiscount
    ? `/checkout?discount=${encodeURIComponent(storedDiscount)}`
    : `${window.Shopify?.routes?.root || '/'}checkout`;
}

    // Items
    renderItems(cart.items);

    // Shipping bar
    updateShippingBar(cart.total_price);

    // Gift nudge
    syncTravelBoxGift(cart);

    // Upsell: hide if all upsell products already in cart
    updateUpsell(cart.items);

    CartEvents.emit('cart:updated', { cart });
  }

  /* ── Items ──────────────────────────────────────────────────── */
  function renderItems(items) {
    const empty = !items || items.length === 0;
    if (emptyEl) emptyEl.style.display = empty ? 'flex' : 'none';
    if (itemsList) itemsList.style.display = empty ? 'none' : '';
    if (!itemsList) return;

    itemsList.innerHTML = items.map((item, idx) => itemHTML(item, idx + 1)).join('');

    // Bind qty buttons
    itemsList.querySelectorAll('[data-qty-btn]').forEach(btn => {
      btn.addEventListener('click', handleQtyClick);
    });
    itemsList.querySelectorAll('[data-remove-btn]').forEach(btn => {
      btn.addEventListener('click', handleRemoveClick);
    });
  }

  function itemHTML(item, line) {
    const isFreeGift = item.properties && item.properties._free_gift;
    const img = item.image
      ? item.image.replace('http://', 'https://')
      : 'https://cdn.shopify.com/s/images/admin/no-image-medium.gif';
    const price = formatMoney(item.final_line_price);
    const compare = item.original_line_price !== item.final_line_price
      ? `<span class="cd-item__price--compare">${formatMoney(item.original_line_price)}</span>` : '';

    return `
      <li class="cd-item" data-key="${item.key}" data-line="${line}">
        <img class="cd-item__img" src="${img}" alt="${escHTML(item.product_title)}" loading="lazy" width="72" height="72">
        <div class="cd-item__info">
          <p class="cd-item__title">${escHTML(item.product_title)}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cd-item__variant">${escHTML(item.variant_title)}</p>` : ''}
          ${isFreeGift ? `
  <div class="cd-item__gift-label">Free gift</div>
` : `
  <div class="cd-item__qty">
    <button class="cd-item__qty-btn" data-qty-btn data-action="decrement"
      data-line="${line}" data-key="${item.key}" data-qty="${item.quantity}"
      aria-label="Decrease quantity">
      ${item.quantity === 1
        ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>`
        : '−'}
    </button>
    <span class="cd-item__qty-val">${item.quantity}</span>
    <button class="cd-item__qty-btn" data-qty-btn data-action="increment"
      data-line="${line}" data-key="${item.key}" data-qty="${item.quantity}"
      aria-label="Increase quantity">+</button>
  </div>
`}
        </div>
        <div class="cd-item__right">
          ${isFreeGift ? '' : `
  <button class="cd-item__remove" data-remove-btn data-line="${line}" data-key="${item.key}" aria-label="Remove item">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    </svg>
  </button>
`}
          <div class="cd-item__price-wrap">
            ${compare}
            <span class="cd-item__price">${price}</span>
          </div>
        </div>
      </li>`;
  }

  function escHTML(str) {
    return String(str || '').replace(/[&<>"']/g, c => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[c]));
  }

  /* ── Qty / Remove handlers ──────────────────────────────────── */
  const debouncedChange = debounce(async ({ key, quantity, line }) => {
    setLoading(true);
    try {
      const cart = await CartAPI.changeByKey(key, quantity);
      renderCart(cart);
      CartEvents.emit('cart:item-changed', { key, quantity, cart });
      if (quantity === 0) CartEvents.emit('cart:item-removed', { key, cart });
    } catch (e) {
      showToast('Could not update cart. Please try again.');
      CartEvents.emit('cart:error', { error: e });
    } finally {
      setLoading(false);
    }
  }, 350);

  function handleQtyClick(e) {
    const btn   = e.currentTarget;
    const key   = btn.dataset.key;
    const line  = parseInt(btn.dataset.line, 10);
    let qty     = parseInt(btn.dataset.qty, 10);
    const dir   = btn.dataset.action;

    qty = dir === 'increment' ? qty + 1 : qty - 1;
    if (qty < 0) return;

    // Optimistic UI
    const li = btn.closest('.cd-item');
    if (li) {
      const valEl = li.querySelector('.cd-item__qty-val');
      if (valEl) valEl.textContent = qty;
      li.querySelectorAll('[data-qty-btn]').forEach(b => (b.dataset.qty = qty));
      if (qty === 0) li.style.opacity = '.4';
    }

    debouncedChange({ key, quantity: qty, line });
  }

  function handleRemoveClick(e) {
    const btn = e.currentTarget;
    const key = btn.dataset.key;
    const li  = btn.closest('.cd-item');
    if (li) li.style.opacity = '.4';
    debouncedChange({ key, quantity: 0 });
  }

  /* ── Shipping bar ───────────────────────────────────────────── */
  function updateShippingBar(totalCents) {
  if (!shippingBar) return;

  let threshold = Number(CFG.freeShippingThreshold || shippingBar.dataset.threshold || 0);

  // Allows both 50 and 5000
  if (threshold > 0 && threshold < 1000) {
    threshold = threshold * 100;
  }

  if (!threshold) return;

  const pct = Math.min((totalCents / threshold) * 100, 100);
  const remaining = formatMoney(Math.max(threshold - totalCents, 0));
const shippingCard = document.querySelector('.cd-shipping-bar__card');

if (shippingCard) {
  const isFilled = totalCents >= threshold;

  shippingCard.classList.toggle('ship_filled', isFilled);
  shippingCard.classList.toggle('unfilled', !isFilled);
}
  if (shippingFill) {
    shippingFill.style.width = `${pct}%`;
  }

  if (shippingMsg) {
    if (totalCents >= threshold) {
      shippingMsg.innerHTML = CFG.freeShippingUnlocked || 'Congrats! You have got FREE SHIPPING!'; 
    } else {
      shippingMsg.innerHTML = (CFG.freeShippingProgress || "You're [[amount]] away from <strong>FREE SHIPPING!</strong>")
        .replace('[[amount]]', `<strong>${remaining}</strong>`)
        .replace('{{amount}}', `<strong>${remaining}</strong>`)
        .replace('{amount}', `<strong>${remaining}</strong>`);
    }
  }

  const badgeAmount = document.getElementById('cd-shipping-badge-amount');
  const badgeLabel = document.getElementById('cd-shipping-badge-label');

  if (badgeAmount) badgeAmount.textContent = totalCents >= threshold ? '✓' : remaining;
  if (badgeLabel) badgeLabel.textContent = totalCents >= threshold ? 'free' : 'to go';
}

  /* ── Gift nudge ─────────────────────────────────────────────── */
  /* ── Gift nudge ─────────────────────────────────────────────── */
let giftSyncing = false;

function getCartTotalExcludingGift(cart) {
  const giftVariantId = Number(CFG.travelBoxVariantId);

  return cart.items
    .filter(item => item.variant_id !== giftVariantId)
    .reduce((sum, item) => sum + item.final_line_price, 0);
}

function getTravelBoxItem(cart) {
  const giftVariantId = Number(CFG.travelBoxVariantId);
  return cart.items.find(item => item.variant_id === giftVariantId);
}

async function syncTravelBoxGift(cart) {
  if (!giftNudge || !CFG.freeGiftEnabled) return;

  const giftVariantId = Number(CFG.travelBoxVariantId);

  // £50 = 5000 cents
  let threshold = Number(CFG.freeGiftThreshold || 5000);

  // Allows config value as 50 or 5000
  if (threshold > 0 && threshold < 1000) {
    threshold = threshold * 100;
  }

  if (!giftVariantId || !threshold) return;

  const cartTotalExcludingGift = getCartTotalExcludingGift(cart);
  const giftItem = getTravelBoxItem(cart);
  const qualifies = cartTotalExcludingGift >= threshold;

  updateGiftNudgeText(cartTotalExcludingGift, threshold, qualifies, !!giftItem);

  if (giftSyncing) return;

  try {
    giftSyncing = true;

    if (qualifies && !giftItem) {
      await CartAPI.add({
        id: giftVariantId,
        quantity: 1,
        properties: {
          _free_gift: 'Travel Box'
        }
      });

      const updatedCart = await CartAPI.get();
      renderCart(updatedCart);
      return;
    }

    if (!qualifies && giftItem) {
      await CartAPI.changeByKey(giftItem.key, 0);

      const updatedCart = await CartAPI.get();
      renderCart(updatedCart);
      return;
    }

    if (qualifies && giftItem.quantity > 1) {
      await CartAPI.changeByKey(giftItem.key, 1);

      const updatedCart = await CartAPI.get();
      renderCart(updatedCart);
      return;
    }
  } catch (err) {
    console.warn('[CartDrawer] Gift sync failed:', err);
  } finally {
    giftSyncing = false;
  }
}

function updateGiftNudgeText(cartTotal, threshold, qualifies, hasGift) {
  if (!giftNudge || !giftText) return;

  giftNudge.style.display = '';

  const remaining = formatMoney(Math.max(threshold - cartTotal, 0));

  if (qualifies && hasGift) {
    giftText.innerHTML = 'FREE Travel Box + Shipping Unlocked';
    giftNudge.classList.add('cd-gift-nudge--unlocked');
    return;
  }

  if (qualifies && !hasGift) {
    giftText.innerHTML = 'Adding your <strong>free travel box…</strong>';
    giftNudge.classList.add('cd-gift-nudge--unlocked');
    return;
  }

  giftText.innerHTML = `Your FREE LUXURY GIFT BOX Awaits At £50+`;
  giftNudge.classList.remove('cd-gift-nudge--unlocked');
}

  /* ── Upsell ─────────────────────────────────────────────────── */
  function updateUpsell(cartItems) {
    const upsellSection = document.getElementById('cd-upsell');
    if (!upsellSection) return;
    if (!upsellState.size) return; // not yet initialised

    const cartVariantIds = new Set(cartItems.map(i => i.variant_id));

    // Deselect cards whose chosen variant is already in the cart
    document.querySelectorAll('#cd-upsell-cards .cd-upsell__card').forEach(card => {
      const handle = card.dataset.handle;
      const state  = upsellState.get(handle);
      if (!state) return;
      if (cartVariantIds.has(state.variantId)) {
        state.selected = false;
        upsellState.set(handle, state);
        card.classList.remove('cd-upsell__card--selected');
        card.classList.add('cd-upsell__card--deselected');
        card.setAttribute('aria-checked', 'false');
      }
    });

    // Hide section only when every tracked variant is already in cart
    const allIn = upsellState.size > 0 &&
      [...upsellState.values()].every(s => cartVariantIds.has(s.variantId));
    upsellSection.style.display = allIn ? 'none' : '';

    refreshUpsellButton();
  }

  /* ══════════════════════════════════════════════════════════════
     UPSELL — selectable cards with real variant fetching
  ══════════════════════════════════════════════════════════════ */

  /**
   * State map: handle → { selected: bool, variantId: number, price: number }
   * Populated when product JSON resolves.
   */
  const upsellState = new Map();

  async function initUpsell() {
    const cards = document.querySelectorAll('#cd-upsell-cards .cd-upsell__card');
    if (!cards.length) return;

    // Fetch all products in parallel
    await Promise.all([...cards].map(card => loadUpsellCard(card)));

    // Bind card toggle (click anywhere on card toggles selection)
    cards.forEach(card => {
      card.addEventListener('click',   handleCardToggle);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardToggle.call(card, e); }
      });
    });

    // Initial button label
    refreshUpsellButton();
  }

  /** Fetch /products/{handle}.js and render variant <select> */
  async function loadUpsellCard(card) {
    const handle    = card.dataset.handle;
    const variantSlot = card.querySelector('[data-variant-slot]');
    const priceSlot   = card.querySelector('[data-price-slot]');
    if (!handle) return;

    try {
      const res     = await fetch(`/products/${handle}.js`);
      const product = await res.json();
      const variants = product.variants || [];

      // Default: first available variant, or first overall
      const defaultVariant = variants.find(v => v.available) || variants[0];
      if (!defaultVariant) return;

      // Seed state (selected by default)
      upsellState.set(handle, {
  selected: false,
  variantId: defaultVariant.id,
  price: defaultVariant.price,
  compareAtPrice: defaultVariant.compare_at_price || defaultVariant.price,
});

      // Render variant selector
      if (variantSlot) {
        if (variants.length === 1) {
          // Single variant — just show the title if it's not "Default Title"
          variantSlot.innerHTML = variants[0].title !== 'Default Title'
            ? `<span style="font-size:11px;color:var(--cd-muted)">${escHTML(variants[0].title)}</span>`
            : '';
        } else {
          const select = document.createElement('select');
          select.className = 'cd-upsell__variant-select';
          select.setAttribute('aria-label', `${product.title} variant`);

          variants.forEach(v => {
            const opt = document.createElement('option');
            opt.value       = v.id;
            opt.textContent = v.title;
            opt.disabled    = !v.available;
            if (v.id === defaultVariant.id) opt.selected = true;
            select.appendChild(opt);
          });

          // On variant change: update state price + label, stop card toggle
          select.addEventListener('change', (e) => {
            e.stopPropagation(); // don't toggle card selection
            const chosen = variants.find(v => v.id === parseInt(e.target.value, 10));
            if (chosen) {
              const s = upsellState.get(handle);
              if (s) {
                s.variantId = chosen.id;
                s.price = chosen.price;
s.compareAtPrice = chosen.compare_at_price || chosen.price;
                upsellState.set(handle, s);
              }
              if (priceSlot) priceSlot.textContent = formatMoney(chosen.price);
            }
          });

          // Prevent click on select from toggling the card
          select.addEventListener('click', (e) => e.stopPropagation());

          variantSlot.innerHTML = '';
          variantSlot.appendChild(select);
        }
      }

      // Show correct price for default variant
      if (priceSlot) priceSlot.textContent = formatMoney(defaultVariant.price);

    } catch (err) {
      console.warn('[CartDrawer] Could not load upsell product:', handle, err);
      if (variantSlot) variantSlot.innerHTML = '';
    }
  }

  /** Toggle card selected / deselected */
  function handleCardToggle(e) {
    // If click originated from the variant <select>, bail
    if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') return;

    const card   = e.currentTarget || this;
    const handle = card.dataset.handle;
    const state  = upsellState.get(handle);
    if (!state) return;

    state.selected = !state.selected;
    upsellState.set(handle, state);

    card.classList.toggle('cd-upsell__card--selected',   state.selected);
    card.classList.toggle('cd-upsell__card--deselected', !state.selected);
    card.setAttribute('aria-checked', state.selected ? 'true' : 'false');
    
    refreshUpsellButton();
  }

  /** Update "Add X to cart" label and disabled state */
 function refreshUpsellButton() {
  if (!upsellBtn) return;

  const selected = [...upsellState.values()].filter(s => s.selected);
  const count = selected.length;

  const totalPrice = selected.reduce((sum, s) => sum + Number(s.price || 0), 0);
  const totalCompare = selected.reduce((sum, s) => {
    return sum + Number(s.compareAtPrice || s.price || 0);
  }, 0);

  const totalSave = Math.max(totalCompare - totalPrice, 0);
const discountPercent = totalCompare > 0
  ? Math.round((totalSave / totalCompare) * 100)
  : 0;

if (upsellBadge) {
  if (discountPercent > 0) {
    upsellBadge.textContent = `Save ${discountPercent}%`;
    upsellBadge.style.display = '';
  } else {
    upsellBadge.textContent = '';
    upsellBadge.style.display = 'none';
  }
}
  if (upsellSummaryCompare) {
    upsellSummaryCompare.textContent = formatMoney(totalCompare);
    upsellSummaryCompare.style.display = totalSave > 0 ? '' : 'none';
  }

  if (upsellSummaryPrice) {
    upsellSummaryPrice.textContent = formatMoney(totalPrice);
  }

  if (upsellSummarySave) {
    upsellSummarySave.textContent = totalSave > 0
      ? `You Save ${formatMoney(totalSave)}`
      : '';
    upsellSummarySave.style.display = totalSave > 0 ? '' : 'none';
  }

  if (count === 0) {
    upsellBtn.textContent = 'Select at least one item';
    upsellBtn.disabled = true;
  } else {
    upsellBtn.textContent = count === 1
      ? `Add 1 item to cart`
      : `Add ${count} items to cart`;
    upsellBtn.disabled = false;
  }
}

  /** Add selected upsell products to cart with chosen variant IDs */
  if (upsellBtn) {
    upsellBtn.addEventListener('click', async () => {
      const toAdd = [...upsellState.entries()]
        .filter(([, s]) => s.selected)
        .map(([, s]) => ({ id: s.variantId, quantity: 1 }));

      if (!toAdd.length) return;

      upsellBtn.disabled    = true;
      upsellBtn.textContent = 'Adding…';
      setLoading(true);

      try {
        await CartAPI.add(toAdd);
        const cart = await CartAPI.get();
        renderCart(cart);
        CartEvents.emit('cart:item-added', { items: toAdd, cart });
        showToast(`${toAdd.length} item${toAdd.length > 1 ? 's' : ''} added to cart!`);
        // Update cards: deselect items that are now in cart
        updateUpsell(cart.items);
      } catch (err) {
        showToast(err.message || 'Could not add items. Please try again.');
        CartEvents.emit('cart:error', { error: err });
        upsellBtn.disabled = false;
        refreshUpsellButton();
      } finally {
        setLoading(false);
      }
    });
  }

  /* ── Discount code ──────────────────────────────────────────── */
  if (discountApply && discountInput) {
  discountApply.addEventListener('click', async () => {
    const code = discountInput.value.trim();
    if (!code) return;

    discountApply.disabled = true;
    discountApply.textContent = 'Applying…';
    setLoading(true);

    try {
      const cart = await CartAPI.applyDiscount(code);

      sessionStorage.setItem('cd_discount', code);

      if (checkoutBtn) {
        checkoutBtn.href = `/checkout?discount=${encodeURIComponent(code)}`;
      }

      renderCart(cart);

      if (discountMsg) {
        renderDiscountPill(code);
        discountMsg.className = 'cd-discount__msg cd-discount__msg--success';
      }
    } catch (err) {
      sessionStorage.removeItem('cd_discount');

      if (discountMsg) {
        discountMsg.textContent = err.message || 'Discount code could not be applied.';
        discountMsg.className = 'cd-discount__msg cd-discount__msg--error';
      }
    } finally {
      discountApply.disabled = false;
      discountApply.textContent = 'Apply';
      setLoading(false);
    }
  });

  discountInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') discountApply.click();
  });
}
function renderDiscountPill(code) {
  if (!discountMsg) return;

  discountMsg.innerHTML = `
    <span class="cd-discount-row">
      <span>Discounts</span>
      <span class="cd-discount-pill">
        <span class="cd-discount-pill__icon">◇</span>
        <strong>${escHTML(code)}</strong>
        <button type="button" class="cd-discount-pill__remove" aria-label="Remove discount">×</button>
      </span>
    </span>
  `;

  discountMsg.className = 'cd-discount__msg cd-discount__msg--success';
}
discountMsg && discountMsg.addEventListener('click', async (e) => {
  const removeBtn = e.target.closest('.cd-discount-pill__remove');
  if (!removeBtn) return;

  removeBtn.disabled = true;
  setLoading(true);

  try {
    sessionStorage.removeItem('cd_discount');
    discountInput.value = '';

    await CartAPI.applyDiscount('');

    const cart = await CartAPI.get();
    renderCart(cart);

    discountMsg.innerHTML = '';
    discountMsg.className = 'cd-discount__msg';
  } catch (err) {
    discountMsg.textContent = err.message || 'Could not remove discount.';
    discountMsg.className = 'cd-discount__msg cd-discount__msg--error';
  } finally {
    setLoading(false);
  }
});
function getCartDiscountCode(cart) {
  if (!cart) return null;

  if (cart.cart_level_discount_applications?.length) {
    return cart.cart_level_discount_applications[0].title;
  }

  if (cart.discount_applications?.length) {
    return cart.discount_applications[0].title;
  }

  return sessionStorage.getItem('cd_discount');
}
  /* ══════════════════════════════════════════════════════════════
     INTERCEPT .shopify-product-form SUBMISSIONS
     ─────────────────────────────────────────────────────────────
     We use capture phase (true) so we get the event BEFORE the
     theme's own handlers. We stop propagation to kill theme events,
     then handle add-to-cart ourselves.
  ══════════════════════════════════════════════════════════════ */

  /** Kill any existing theme AJAX-cart listeners by replacing nodes */
  function neutraliseThemeForms() {
    document.querySelectorAll('.shopify-product-form, [data-type="add-to-cart-form"], form[action*="/cart/add"]')
      .forEach(form => {
        // Clone replaces the node, detaching ALL old event listeners
        const clone = form.cloneNode(true);
        form.parentNode.replaceChild(clone, form);
        bindFormInterceptor(clone);
      });
  }

  function bindFormInterceptor(form) {
    form.addEventListener('submit', handleFormSubmit, { capture: true });
    // Some themes use click on the submit button instead of submit event
    const btns = form.querySelectorAll('[type="submit"], [name="add"], .btn-add-to-cart, .product-form__submit');
    btns.forEach(btn => btn.addEventListener('click', (e) => {
      // Let the submit event fire; handled there
    }));
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    e.stopImmediatePropagation(); // stop theme handlers

    const form = e.currentTarget;
    const submitBtn = form.querySelector('[type="submit"]');

    // Read variant ID
    let variantId = null;
    const variantInput = form.querySelector('[name="id"]');
    if (variantInput) variantId = variantInput.value;

    // Read quantity
    let quantity = 1;
    const qtyInput = form.querySelector('[name="quantity"]');
    if (qtyInput) quantity = parseInt(qtyInput.value, 10) || 1;

    // Read custom properties
    const properties = {};
    form.querySelectorAll('[name^="properties["]').forEach(input => {
      const match = input.name.match(/properties\[(.+?)\]/);
      if (match) properties[match[1]] = input.value;
    });

    if (!variantId) {
      showToast('Please select options before adding to cart.');
      return;
    }

    // Disable submit button
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn._originalText = submitBtn.innerHTML;
      submitBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:6px"><span style="width:14px;height:14px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:cd-spin .7s linear infinite;display:inline-block"></span> Adding…</span>';
    }

    setLoading(true);

    try {
      const item = { id: parseInt(variantId, 10), quantity };
      if (Object.keys(properties).length) item.properties = properties;

      await CartAPI.add(item);
      const cart = await CartAPI.get();
      renderCart(cart);
      CartEvents.emit('cart:item-added', { item, cart });
      openDrawer();
    } catch (err) {
      const msg = err.message || 'Unable to add to cart. Please try again.';
      showToast(msg);
      CartEvents.emit('cart:error', { error: err });
    } finally {
      setLoading(false);
      if (submitBtn) {
        submitBtn.disabled = false;
        if (submitBtn._originalText) submitBtn.innerHTML = submitBtn._originalText;
      }
    }
  }

  /* ── Watch for dynamically injected forms (quick-view etc.) ─── */
  const formObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        const forms = node.matches('.shopify-product-form, [data-type="add-to-cart-form"], form[action*="/cart/add"]')
          ? [node]
          : [...node.querySelectorAll('.shopify-product-form, [data-type="add-to-cart-form"], form[action*="/cart/add"]')];
        forms.forEach(bindFormInterceptor);
      });
    });
  });
  formObserver.observe(document.body, { childList: true, subtree: true });

  /* ── Also expose openDrawer for header cart icon clicks ─────── */
  window.openCartDrawer  = openDrawer;
  window.closeCartDrawer = closeDrawer;
  window.refreshCart     = async () => {
    const cart = await CartAPI.get();
    renderCart(cart);
    return cart;
  };

  /* ── Bind header cart icon ──────────────────────────────────── */
  function bindHeaderCartIcons() {
    const selectors = [
      'a[href="/cart"]',
      '.cart-link',
      '.header__cart',
      '[data-cart-toggle]',
      '.cart-icon-bubble',
      '#cart-icon-bubble',
    ];
    document.querySelectorAll(selectors.join(',')).forEach(el => {
      // Only intercept icon links, not "View Cart" page links elsewhere
      if (el.tagName === 'A' && el.pathname === '/cart') {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          openDrawer();
        });
      }
    });
  }

  /* ══════════════════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════════════════ */
  async function init() {
    // Neutralise theme forms (clones + rebinds)
    neutraliseThemeForms();

    // Bind header icons
    bindHeaderCartIcons();

    // Initialise upsell cards (fetches product JSON + builds variant selectors)
    initUpsell();

    // Fetch and render current cart state
    try {
      const cart = await CartAPI.get();
      renderCart(cart);
    } catch (_) {
      // silently ignore on cold load
    }

    // Re-bind after theme navigation (SPA themes)
    document.addEventListener('page:load',  init);      // Turbo / theme-specific
    document.addEventListener('shopify:section:load', neutraliseThemeForms);
  }

  /* Run when DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

(function () {
  function openGiftPopup() {
    var popup = document.getElementById('cd-gift-popup');
    if (!popup) return;

    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cd-gift-popup-open');
  }

  function closeGiftPopup() {
    var popup = document.getElementById('cd-gift-popup');
    if (!popup) return;

    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cd-gift-popup-open');
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('#cd-gift-nudge')) {
      event.preventDefault();
      openGiftPopup();
      return;
    }

    if (event.target.closest('[data-gift-popup-close]')) {
      closeGiftPopup();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      closeGiftPopup();
    }
  });
})();